import { Activity, ArrowLeft, Edit, ExternalLink, QrCode } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import AssetInstanceForm from '../components/AssetInstanceForm';
import Header from '../components/Header';
import MapView from '../components/MapView';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import ScanHistoryCard from '../components/ScanHistoryCard';
import RentalHistory from './RentalHistory';

type Instance = {
  id: string;
  instance_number: string;
  serial_number?: string;
  current_status: string;
  current_condition: string;
  acquisition_cost?: number;
  acquisition_date?: string;
  notes?: string;
  current_location?: { id: string; address_line1: string; city?: string; region?: string; postal_code?: string; country_code: string; latitude?: number | null; longitude?: number | null } | null;
  template?: { id: string; name: string; template_code?: string; default_condition?: string } | null;
  qr_code?: { id: string; code: string; status?: string } | null;
};

export default function UnitDetails() {
  const { unitId } = useParams<{ unitId: string }>();
  const [unit, setUnit] = useState<Instance | null>(null);
  const [user, setUser] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [locations, setLocations] = useState<any[]>([]);
  const [addLocOpen, setAddLocOpen] = useState(false);
  const [newLoc, setNewLoc] = useState({ address_line1: '', address_line2: '', city: '', region: '', postal_code: '', country_code: 'GB', address_type: 'warehouse', latitude: '', longitude: '' });
  const [mapLat, setMapLat] = useState<number | undefined>(undefined);
  const [mapLon, setMapLon] = useState<number | undefined>(undefined);
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [editOpen, setEditOpen] = useState(false);

  useEffect(() => {
    try { const u = localStorage.getItem('user'); if (u) setUser(JSON.parse(u)); } catch {}
  }, []);

  const loadUnit = async () => {
    try {
      if (!unitId) return;
      setLoading(true);
      const token = localStorage.getItem('token');
      const res = await fetch(`/api/asset-instances/${unitId}`, { headers: { Authorization: `Bearer ${token}` } });
      const data = await res.json();
      setUnit(data.success ? data.data : data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadUnit(); }, [unitId]);

  useEffect(() => {
    if (searchParams.get('edit') === '1') setEditOpen(true);
  }, [searchParams]);

  useEffect(() => {
    (async () => {
      try {
        const token = localStorage.getItem('token');
        const res = await fetch('/api/addresses', { headers: { Authorization: `Bearer ${token}` } });
        const data = await res.json();
        setLocations(data.success ? data.data : data);
      } catch {}
    })();
  }, []);

  // Derive map coordinates from address (prefer stored lat/lon; fallback to geocoding)
  useEffect(() => {
    const loc = unit?.current_location;
    if (!loc) { setMapLat(undefined); setMapLon(undefined); return; }
    if (loc.latitude != null && loc.longitude != null) {
      setMapLat(Number(loc.latitude));
      setMapLon(Number(loc.longitude));
      return;
    }
    // Fallback geocode with Nominatim
    const q = [loc.address_line1, loc.city, loc.postal_code, loc.country_code].filter(Boolean).join(', ');
    (async () => {
      try {
        const url = `https://nominatim.openstreetmap.org/search?format=json&limit=1&q=${encodeURIComponent(q)}`;
        const res = await fetch(url, { headers: { 'Accept-Language': 'en' } });
        const json = await res.json();
        if (Array.isArray(json) && json[0]?.lat && json[0]?.lon) {
          setMapLat(Number(json[0].lat));
          setMapLon(Number(json[0].lon));
        }
      } catch {}
    })();
  }, [unit?.current_location]);

  const canEdit = user?.roles?.some((r: any) => ['admin', 'supervisor'].includes(r.key));

  if (loading) return (
    <div className="min-h-screen bg-background">
      <Header user={user} currentPage="assets" />
      <main className="container mx-auto px-4 py-8">Loading unit…</main>
    </div>
  );

  if (!unit) return (
    <div className="min-h-screen bg-background">
      <Header user={user} currentPage="assets" />
      <main className="container mx-auto px-4 py-8">Unit not found.</main>
    </div>
  );

  return (
    <div className="min-h-screen bg-background">
      <Header user={user} currentPage="assets" />
      <main className="container mx-auto px-4 py-8">
        <div className="flex items-start justify-between mb-6">
          <div>
            <button onClick={() => navigate(-1)} className="inline-flex items-center text-sm text-muted-foreground hover:text-primary mb-3">
              <ArrowLeft className="h-4 w-4 mr-2" /> Back
            </button>
            <h1 className="text-3xl font-heading font-bold text-foreground">Unit {unit.instance_number}</h1>
            <div className="mt-1 text-sm text-muted-foreground">
              {unit.template?.template_code && (
                <span className="font-mono mr-2">{unit.template.template_code}</span>
              )}
              Status & condition overview
            </div>
          </div>
          {canEdit && (
            <Button onClick={() => setEditOpen(true)}>
              <Edit className="h-4 w-4 mr-2" /> Edit Unit
            </Button>
          )}
        </div>

        <div className="space-y-6">
          <Card>
              <CardHeader>
                <CardTitle>Quick facts</CardTitle>
                <CardDescription>Current state and metadata</CardDescription>
              </CardHeader>
              <CardContent className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <div className="text-xs uppercase tracking-wide font-bold text-muted-foreground">Status</div>
                  <div className="mt-1"><Badge variant={unit.current_status === 'available' ? 'default' : 'secondary'}>{unit.current_status}</Badge></div>
                </div>
                <div>
                  <div className="text-xs uppercase tracking-wide font-bold text-muted-foreground">Condition</div>
                  <div className="mt-1"><Badge variant={unit.current_condition === 'damaged' ? 'destructive' : 'secondary'}>{unit.current_condition}</Badge></div>
                </div>
                <div>
                  <div className="text-xs uppercase tracking-wide font-bold text-muted-foreground">QR Code</div>
                  <div className="mt-1 flex items-center gap-2">
                    {unit.qr_code ? (
                      <>
                        <QrCode className="h-4 w-4 text-muted-foreground" />
                        <Link to={`/qr-codes/${unit.qr_code.id}`} className="text-primary hover:underline">{unit.qr_code.code}</Link>
                      </>
                    ) : (
                      <span className="text-muted-foreground">QR is not assigned</span>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>

          <div className="space-y-6">
              <div>
                <div className="flex items-center mb-2 text-sm text-muted-foreground"><Activity className="h-4 w-4 mr-2" />Rental cycles</div>
                <RentalHistory unitId={unit.id} />
              </div>
              <div>
                <div className="flex items-center mb-2 text-sm text-muted-foreground"><Activity className="h-4 w-4 mr-2" />Movement audit</div>
                <div className="rounded-md border p-4 text-sm text-muted-foreground">Tracking events timeline coming next.</div>
              </div>
            </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Card>
                <CardHeader>
                  <CardTitle>Current address</CardTitle>
                  <CardDescription>Assign a saved address or add a new one</CardDescription>
                </CardHeader>
                <CardContent className="space-y-3">
                  {unit.current_location ? (
                    <div className="text-sm">
                      <div className="font-medium">{unit.current_location.address_line1}</div>
                      {unit.current_location.city && <div>{unit.current_location.city}{unit.current_location.region ? `, ${unit.current_location.region}` : ''}</div>}
                      <div>{unit.current_location.postal_code || ''} {unit.current_location.country_code}</div>
                      <a
                        className="inline-flex items-center text-xs text-primary underline mt-2"
                        href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(`${unit.current_location.address_line1} ${unit.current_location.city || ''} ${unit.current_location.postal_code || ''}`)}`}
                        target="_blank" rel="noreferrer"
                      >
                        Open in Maps <ExternalLink className="h-3 w-3 ml-1" />
                      </a>
                    </div>
                  ) : (
                    <div className="text-sm text-muted-foreground">No address assigned.</div>
                  )}

                  <div className="space-y-2">
                    <Label>Assign location</Label>
                    <Select value={(unit.current_location?.id as any) || '__none__'} onValueChange={async (v) => {
                      const token = localStorage.getItem('token');
                      const res = await fetch(`/api/asset-instances/${unit.id}`, {
                        method: 'PUT',
                        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                        body: JSON.stringify({ current_location_id: v === '__none__' ? null : v }),
                      });
                      if (res.ok) {
                        const data = await res.json();
                        const updated = data.success ? data.data : data;
                        setUnit(updated);
                        // Update map coordinates if available
                        const loc = updated.current_location;
                        if (loc?.latitude != null && loc?.longitude != null) {
                          setMapLat(Number(loc.latitude));
                          setMapLon(Number(loc.longitude));
                        }
                      }
                    }}>
                      <SelectTrigger><SelectValue placeholder="Select location" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__none__">No location</SelectItem>
                        {locations.map((loc) => (
                          <SelectItem key={loc.id} value={loc.id}>{loc.address_line1}{loc.city ? `, ${loc.city}` : ''}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Button type="button" variant="outline" size="sm" onClick={() => setAddLocOpen(true)}>Add address</Button>
                  </div>
                </CardContent>
              </Card>

              <div>
                <MapView lat={mapLat ?? undefined} lon={mapLon ?? undefined} height={320} />
              </div>
            </div>

          <Dialog open={addLocOpen} onOpenChange={setAddLocOpen}>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Add address</DialogTitle>
                  <DialogDescription>Create a new address for this unit</DialogDescription>
                </DialogHeader>
                <div className="grid grid-cols-1 gap-3">
                  <div>
                    <Label>Address Line 1</Label>
                    <Input value={newLoc.address_line1} onChange={(e) => setNewLoc({ ...newLoc, address_line1: e.target.value })} />
                  </div>
                  <div>
                    <Label>Address Line 2</Label>
                    <Input value={newLoc.address_line2} onChange={(e) => setNewLoc({ ...newLoc, address_line2: e.target.value })} />
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                    <div>
                      <Label>City</Label>
                      <Input value={newLoc.city} onChange={(e) => setNewLoc({ ...newLoc, city: e.target.value })} />
                    </div>
                    <div>
                      <Label>Region</Label>
                      <Input value={newLoc.region} onChange={(e) => setNewLoc({ ...newLoc, region: e.target.value })} />
                    </div>
                    <div>
                      <Label>Postal Code</Label>
                      <Input value={newLoc.postal_code} onChange={(e) => setNewLoc({ ...newLoc, postal_code: e.target.value })} />
                    </div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                    <div>
                      <Label>Country</Label>
                      <Input maxLength={2} value={newLoc.country_code} onChange={(e) => setNewLoc({ ...newLoc, country_code: e.target.value.toUpperCase() })} />
                    </div>
                    <div className="md:col-span-2">
                      <Label>Type</Label>
                      <Select value={newLoc.address_type} onValueChange={(v) => setNewLoc({ ...newLoc, address_type: v })}>
                        <SelectTrigger><SelectValue placeholder="Select type" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="warehouse">Warehouse</SelectItem>
                          <SelectItem value="hub">Hub</SelectItem>
                          <SelectItem value="customer_site">Customer Site</SelectItem>
                          <SelectItem value="vendor">Vendor</SelectItem>
                          <SelectItem value="other">Other</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div>
                      <Label>Latitude (optional)</Label>
                      <Input value={newLoc.latitude} onChange={(e) => setNewLoc({ ...newLoc, latitude: e.target.value })} />
                    </div>
                    <div>
                      <Label>Longitude (optional)</Label>
                      <Input value={newLoc.longitude} onChange={(e) => setNewLoc({ ...newLoc, longitude: e.target.value })} />
                    </div>
                  </div>
                </div>
                <DialogFooter>
                  <Button type="button" variant="secondary" onClick={() => setAddLocOpen(false)}>Cancel</Button>
                  <Button type="button" onClick={async () => {
                    if (!newLoc.address_line1 || !newLoc.city || !newLoc.country_code) { window.alert('Address line 1, city and country are required'); return; }
                    const token = localStorage.getItem('token');
                    const res = await fetch('/api/addresses', { method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` }, body: JSON.stringify(newLoc) });
                    const data = await res.json();
                    if (!res.ok || !data.success) { window.alert(data.message || 'Failed to create'); return; }
                    setLocations((prev) => [data.data, ...prev]);
                    setAddLocOpen(false);
                  }}>Save</Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>

          {unit?.id && (
            <ScanHistoryCard
              title="Scan History"
              endpoint={`/api/scan/history/asset/${unit.id}`}
              emptyMessage="No scans recorded for this unit"
            />
          )}
        </div>

        {/* Edit dialog */}
        <Dialog open={editOpen} onOpenChange={(v) => {
          setEditOpen(v);
          if (!v) {
            // Clear query param if present
            if (searchParams.get('edit') === '1') {
              searchParams.delete('edit');
              setSearchParams(searchParams, { replace: true });
            }
          }
        }}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>Edit Unit</DialogTitle>
              <DialogDescription>Update details for unit "{unit.instance_number}"</DialogDescription>
            </DialogHeader>
            <AssetInstanceForm
              templateId={unit.template?.id || ''}
              template={{ id: unit.template?.id || '', name: unit.template?.name || '', template_code: unit.template?.template_code || '', default_condition: (unit.template?.default_condition as any) } as any}
              nextUnitSequence={1}
              instance={unit as any}
              onSuccess={async () => { setEditOpen(false); await loadUnit(); }}
              onCancel={() => setEditOpen(false)}
            />
          </DialogContent>
        </Dialog>
      </main>
    </div>
  );
}


